import { mapPayment } from 'connectors/payments/Payment';
import { mapPriceData } from 'connectors/price/Prices';
import { mapPickupPointsApiData } from 'connectors/transports/pickupPoint/PickupPoint';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import {
    ListedStoreConnectionFragmentApi,
    TransportWithAvailablePaymentsAndStoresFragmentApi,
    useTransportsQueryApi,
} from 'graphql/generated';
import { useMemo } from 'react';
import { MappedTransportType } from 'types/transport';

export const useTransports = (
    cartUuid: string | null,
): { data: MappedTransportType[]; fetching: boolean; isLoaded: boolean } => {
    const { currencyCode } = useDomainConfig();
    const [result] = useTransportsQueryApi({ variables: { cartUuid } });
    const transportsApiData = result.data?.transports;

    return useMemo(() => {
        if (transportsApiData !== undefined) {
            return {
                data: mapTransports(transportsApiData, currencyCode),
                fetching: result.fetching,
                isLoaded: true,
            };
        }

        return { data: [], fetching: result.fetching, isLoaded: false };
    }, [currencyCode, transportsApiData, result.fetching]);
};

export const mapTransport = (
    apiData: TransportWithAvailablePaymentsAndStoresFragmentApi & {
        stores?: ListedStoreConnectionFragmentApi | null;
    },
    currencyCode: string,
): MappedTransportType => {
    return {
        ...apiData,
        description: apiData.description !== null ? apiData.description : '',
        instruction: apiData.instruction !== null ? apiData.instruction : '',
        price: mapPriceData(apiData.price, currencyCode),
        payments: apiData.payments.map((payment) => mapPayment(payment, currencyCode)),
        stores: apiData.stores ? mapPickupPointsApiData(apiData.stores) : [],
    };
};

const mapTransports = (
    apiData: TransportWithAvailablePaymentsAndStoresFragmentApi[],
    currencyCode: string,
): MappedTransportType[] => {
    const mappedTransports: MappedTransportType[] = [];
    for (const transport of apiData) {
        const mappedTransport = mapTransport(transport, currencyCode);
        if (mappedTransport.payments.length > 0) {
            mappedTransports.push(mappedTransport);
        }
    }

    return mappedTransports;
};
